import React from "react"
import { usePage } from "@inertiajs/react"

import { StaffContextSwitcher } from "@/components/layouts/staff-context-switcher"
import { StaffNotifications } from "@/components/layouts/staff-notifications"
import { useStaffNotifications } from "@/components/layouts/staff-realtime"
import {
  StaffOnboardingProgress,
  type SharedOnboardingProgress,
} from "@/components/layouts/staff-onboarding-progress"
import { StaffUserMenu, type StaffMenuUser } from "@/components/layouts/staff-user-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar"
import { BrandLogo } from "@/components/ui/brand-logo"
import { intlLocale, t } from "@/lib/i18n"
import {
  ArrowLeft,
  Banknote,
  Bike,
  BookOpen,
  Boxes,
  CalendarClock,
  ChartColumn,
  ChefHat,
  ClipboardList,
  CreditCard,
  HeartHandshake,
  History,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Package,
  PanelLeft,
  PlusCircle,
  Radio,
  ReceiptText,
  Settings2,
  Tags,
  Store,
  TrendingUp,
  Truck,
  Users,
  UtensilsCrossed,
  X,
} from "lucide-react"

type SharedLocation = {
  id: number
  name: string
} | null

type SharedAvailableLocation = {
  id: number
  name: string
}

type SharedLocationUser = {
  role: string
} | null

/**
 * `feature` names a key from `config/location_features.yml`. An item that has
 * one is shown only while the restaurant has that feature switched on; an item
 * without one is unconditional management furniture.
 *
 * This does not protect the screen — every gated controller carries
 * `Staff::Location::FeatureGuard`, which is what actually turns the request
 * away. The menu only stops advertising a door that is locked, exactly as
 * `staff-layout`'s floor nav does.
 */
type NavItem = {
  titleKey: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  feature?: string
  /**
   * The location roles the destination accepts, mirroring the controller's own
   * predicate. An item without one is open to everyone on the floor.
   */
  roles?: string[]
}

type NavGroup = {
  labelKey: string
  items: NavItem[]
}


const BASE_NAV_ITEMS: NavItem[] = [
  { titleKey: "ui.navigation.home", href: "/manage", icon: LayoutDashboard },
  { titleKey: "ui.navigation.operation", href: "/menu", icon: UtensilsCrossed },
  { titleKey: "ui.navigation.new_location", href: "/locations/new", icon: PlusCircle },
]

/**
 * Management used to be one heading with fifteen links under it, in the order
 * the features happened to ship. That is a filing cabinet with no drawers: the
 * only way to find "Proveedores" was to read every entry.
 *
 * The drawers are what a manager is working ON — the catalog they sell, the
 * stock behind it, the room and the channels they sell through, and the business
 * around all of it. Order **inside** a drawer is alphabetical and computed from
 * the rendered label, never from this array: see `byRenderedLabel`.
 */
const MANAGER_NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "ui.navigation.group_catalog",
    items: [
      { titleKey: "ui.navigation.menu", href: "/manage/menu", icon: UtensilsCrossed },
      // Dish-level counts (#item-stock-counts): how many orders of a tracked
      // dish are left to sell today. Lives beside the menu it narrows rather
      // than in the pantry drawer — it caps *sellable dishes*, not ingredient
      // supply, and shares no data with `inventory`. Ships `default_enabled:
      // false`, so an unconfigured restaurant sees no new link.
      { titleKey: "ui.navigation.item_stocks", href: "/manage/item_stocks", icon: Boxes, feature: "item_stock" },
      // Multi-branch (#320). The central menu is configuration, so it lives
      // here; the comparison is reading, so it sits beside the report instead.
      // Gated on `multi_location`, which ships unavailable — and the server
      // refuses the URL for a restaurant with no organization, so a
      // single-location tenant never learns the concept exists.
      { titleKey: "ui.navigation.central_items", href: "/manage/central_items", icon: Layers, feature: "multi_location" },
      { titleKey: "ui.navigation.promotions", href: "/manage/promotions", icon: Tags, feature: "promotions" },
    ],
  },
  {
    // Inventory (#315) and purchasing (#316) share a drawer because they are one
    // question asked twice: what do we have, and how did it get here. Both ship
    // `default_available: false`, so a restaurant granted neither sees no
    // heading at all — an empty group renders nothing, see below.
    labelKey: "ui.navigation.group_inventory",
    items: [
      { titleKey: "ui.navigation.purchases", href: "/manage/purchases", icon: ClipboardList, feature: "purchasing" },
      { titleKey: "ui.navigation.supplies", href: "/manage/supplies", icon: Package, feature: "inventory" },
      { titleKey: "ui.navigation.suppliers", href: "/manage/suppliers", icon: Truck, feature: "purchasing" },
      // Recipes sit with the pantry rather than with the menu: they are what
      // makes a dish consume stock, and they share `inventory`'s gate.
      { titleKey: "ui.navigation.recipes", href: "/manage/recipes", icon: BookOpen, feature: "inventory" },
    ],
  },
  {
    // How an order reaches the restaurant, and who it comes from. The delivery
    // BOARD and the reservation BOOK are deliberately absent: both are touched
    // during service, so they live beside the KDS rather than in /manage.
    labelKey: "ui.navigation.group_service",
    items: [
      { titleKey: "ui.navigation.channels", href: "/manage/channels", icon: Radio, feature: "external_channels" },
      { titleKey: "ui.navigation.customers", href: "/manage/customers", icon: HeartHandshake, feature: "loyalty" },
      { titleKey: "ui.navigation.tables", href: "/manage/tables", icon: LayoutGrid },
      { titleKey: "ui.navigation.couriers", href: "/manage/couriers", icon: Bike, feature: "own_delivery" },
    ],
  },
  {
    labelKey: "ui.navigation.group_business",
    items: [
      { titleKey: "ui.navigation.billing", href: "/manage/billing", icon: CreditCard },
      { titleKey: "ui.navigation.branch", href: "/manage/location", icon: Store },
      // `cfdi_invoicing` ships `default_available: false`, so this covers both
      // halves of the gate at once: `enabledFeatures` is
      // `Location#enabled_features`, which is `available && enabled`. A
      // restaurant the platform has not granted CFDI to never sees the link, and
      // neither does one that has it and left the switch off.
      { titleKey: "ui.navigation.tax_profile", href: "/manage/tax_profile", icon: ReceiptText, feature: "cfdi_invoicing" },
      { titleKey: "ui.navigation.staff", href: "/manage/users", icon: Users },
    ],
  },
  {
    labelKey: "ui.navigation.monitoring",
    items: [
      // Reading, not configuring — which is why it sits in Monitoreo beside the
      // audit log rather than in one of the drawers above. Its own icon rather
      // than the till report's ChartColumn: two different screens under one
      // glyph is how a manager ends up on the wrong one.
      { titleKey: "ui.staff.stats.title", href: "/manage/stats", icon: TrendingUp, feature: "sales_reports" },
      { titleKey: "ui.navigation.events", href: "/manage/events", icon: History },
    ],
  },
]

/**
 * Alphabetical order is a property of the label on screen, not of the source
 * array: the same list reads in a different order in Spanish and in English, so
 * a hand-sorted array would only ever be right in one of them.
 */
const byRenderedLabel = (a: NavItem, b: NavItem) =>
  t(a.titleKey).localeCompare(t(b.titleKey), intlLocale())

/**
 * `/manage` is a prefix of every management route, so a bare `startsWith` lit
 * "Inicio" on top of the real entry on all fifteen of them. The dashboard
 * matches exactly; everything else keeps prefix matching so a nested path
 * (`/manage/supplies/12`) still highlights its own section.
 *
 * The query string is dropped first: `/manage/stats?range=years` is still the
 * stats screen, and it is the first management route that carries one.
 */
function isActiveHref(url: string, href: string): boolean {
  const path = url.split("?")[0]

  if (href === "/manage") return path === "/manage" || path === "/manage/"

  return path === href || path.startsWith(`${href}/`)
}

/**
 * The floor nav. These nine were the row of icon buttons in the middle of the
 * navbar and the tab bar at the bottom on mobile; deleting the navbar moved them
 * here without changing the list or its order.
 *
 * Order is operational, not alphabetical, and deliberately so: it is the shape of
 * a shift, from the board you open on to the report you close with. The drawers
 * under /manage sort by rendered label because they are a filing cabinet; this is
 * a sequence.
 */
const FLOOR_NAV_ITEMS: NavItem[] = [
  { titleKey: "ui.navigation.dashboard", href: "/dashboard", icon: LayoutDashboard },
  { titleKey: "ui.react.components.layouts.staff_layout.orders", href: "/orders", icon: ClipboardList },
  { titleKey: "ui.react.components.layouts.staff_layout.tables", href: "/tables", icon: LayoutGrid },
  { titleKey: "ui.react.components.layouts.staff_layout.bills", href: "/bills", icon: ReceiptText },
  // Roles mirror Location::User#can_manage_cash_register?.
  { titleKey: "ui.react.components.layouts.staff_layout.till", href: "/shifts", icon: Banknote, feature: "cash_register", roles: ["manager", "cashier"] },
  { titleKey: "ui.react.components.layouts.staff_layout.kds", href: "/kds", icon: ChefHat, feature: "kds" },
  // Beside the till, not under /manage: a report configures nothing, it is read
  // at the end of the night next to the Z. `roles` mirrors the controller's own
  // manager predicate rather than inventing one.
  { titleKey: "ui.react.components.layouts.staff_layout.reports", href: "/reports", icon: ChartColumn, feature: "sales_reports", roles: ["manager"] },
  // Both are worked DURING service, like the KDS, which is why they are here and
  // not in the manager drawers.
  { titleKey: "ui.react.components.layouts.staff_layout.deliveries", href: "/deliveries", icon: Bike, feature: "own_delivery" },
  { titleKey: "ui.react.components.layouts.staff_layout.reservations", href: "/reservations", icon: CalendarClock, feature: "loyalty" },
]

const MANAGE_HREFS: string[] = [
  ...BASE_NAV_ITEMS.map((item) => item.href),
  ...MANAGER_NAV_GROUPS.flatMap((group) => group.items.map((item) => item.href)),
]

/**
 * Which list the sidebar shows. The two sets never merge: a waiter looking for
 * "Cuentas" should not have to read past "Proveedores", and a manager writing
 * next week's menu is not mid-service.
 *
 * Membership is derived from the management list itself rather than from a
 * `/manage` prefix, because two of its entries — the operation menu and the new
 * location form — do not live under that prefix. Deriving it means a route added
 * to the list above cannot forget to teach this function about itself.
 *
 * Non-managers never enter the management context. Every screen in it is behind
 * `require_manager_access` anyway, and dropping a waiter into a three-item list
 * whose way back is manager-only would strand them.
 */
export function isManageContext(url: string, isManager: boolean): boolean {
  if (!isManager) return false

  const path = url.split("?")[0]
  if (path === "/manage" || path.startsWith("/manage/")) return true

  return MANAGE_HREFS.some((href) => path === href || path.startsWith(`${href}/`))
}

/**
 * Stable across the move from the navbar: the Playwright suite selects floor
 * links by `staff-nav-<slug>` and has since they shipped.
 */
function testId(href: string): string {
  return `staff-nav-${href.replace(/^\//, "").replace(/\//g, "-")}`
}

function NavRow({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
}) {
  const label = t(item.titleKey)

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={collapsed ? label : undefined}
      >
        <a
          href={item.href}
          onClick={onNavigate}
          data-testid={testId(item.href)}
          aria-label={label}
          aria-current={active ? "page" : undefined}
        >
          <item.icon />
          <span>{label}</span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * The collapse control. It is the only chrome outside the list, so it sits with
 * the brand at the top of the panel rather than in a header bar — there is no
 * header bar any more. On mobile the same button closes the Sheet, which is what
 * `toggleSidebar` already does there, so it wears an X instead.
 */
function SidebarToggle() {
  const { isMobile, toggleSidebar, state } = useSidebar()
  const label = isMobile
    ? t("ui.navigation.close_menu")
    : state === "collapsed"
      ? t("ui.navigation.expand_sidebar")
      : t("ui.navigation.collapse_sidebar")

  return (
    <button
      type="button"
      onClick={toggleSidebar}
      data-testid="staff-sidebar-toggle"
      aria-label={label}
      title={label}
      className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring md:size-9"
    >
      {isMobile ? <X className="size-4" /> : <PanelLeft className="size-[18px]" />}
      <span className="sr-only">{label}</span>
    </button>
  )
}

/**
 * The one piece of the navbar that had to survive, and only below `md`.
 *
 * On desktop the panel is always on screen, so its own toggle is enough. On a
 * phone the panel is a Sheet, and a Sheet whose only handle lives inside itself
 * cannot be opened: deleting the bar outright left the whole staff app
 * unreachable on mobile. So this is the smallest thing that answers "how do I
 * get to anything" — the trigger, and the branch you are standing in.
 *
 * The trigger also carries the unread dot, because on a phone the bell is behind
 * the Sheet: without it a waiter has to open the drawer to find out whether
 * there was any reason to. A dot rather than the sidebar's count — the number is
 * one tap away, and 44px of touch target has no room for a legible figure beside
 * the glyph. It reads the same feed the bell does, via
 * `StaffRealtimeProvider`; there is still exactly one wire per page.
 */
export function StaffMobileBar() {
  const { toggleSidebar } = useSidebar()
  const { props } = usePage<{ currentLocation?: SharedLocation }>()
  const { notifications } = useStaffNotifications()
  const unread = notifications.length

  return (
    <div className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-1 border-b bg-background px-2 md:hidden print:hidden">
      <button
        type="button"
        onClick={toggleSidebar}
        data-testid="staff-mobile-menu"
        aria-label={
          unread > 0
            ? t("ui.navigation.open_menu_unread", { count: unread })
            : t("ui.navigation.open_menu")
        }
        className="relative flex size-11 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <PanelLeft className="size-5" />
        {unread > 0 && (
          // aria-hidden: the count is already in the button's own name, and a
          // second announcement would just be noise between the label and the
          // action.
          <span
            aria-hidden="true"
            data-testid="staff-mobile-notifications-dot"
            className="pointer-events-none absolute right-1.5 top-1.5 size-2 rounded-full bg-destructive ring-2 ring-background"
          />
        )}
      </button>
      {props.currentLocation && (
        <span className="min-w-0 truncate text-base font-semibold tracking-tight">
          {props.currentLocation.name}
        </span>
      )}
    </div>
  )
}

export function StaffSidebar() {
  const { url, props } = usePage<{
    user: StaffMenuUser | null
    currentLocation?: SharedLocation
    availableLocations?: SharedAvailableLocation[]
    locationUser?: SharedLocationUser
    enabledFeatures?: string[]
    onboardingProgress?: SharedOnboardingProgress | null
  }>()
  const {
    user,
    locationUser,
    currentLocation,
    // `availableLocations` is no longer read here: the switcher renders
    // organizations as well as branches, and that list comes from `navContext`.
    enabledFeatures = [],
    onboardingProgress,
  } = props

  const { isMobile, setOpenMobile, state } = useSidebar()
  const collapsed = state === "collapsed" && !isMobile
  const isManager = locationUser?.role === "manager"
  const manageContext = isManageContext(url, isManager)

  // A tap inside the mobile Sheet has to dismiss it; on desktop the panel stays.
  const onNavigate = isMobile ? () => setOpenMobile(false) : undefined

  // Both are absent outside a location context (the location picker, staff
  // invites), where a gated item has nothing to point at anyway. Neither of these
  // protects the screen — every gated controller carries its own guard. The menu
  // only stops advertising a door that is locked.
  const visible = (item: NavItem) => {
    if (item.feature && !enabledFeatures.includes(item.feature)) return false
    if (item.roles && !(locationUser?.role && item.roles.includes(locationUser.role))) return false
    return true
  }

  return (
    <Sidebar collapsible="icon" className="print:hidden">
      <SidebarHeader className="gap-0 p-2">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1">
          <a
            href="/dashboard"
            aria-label={t("ui.app.name")}
            className="flex h-11 min-w-0 items-center gap-2 rounded-md px-1 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring md:h-9 group-data-[collapsible=icon]:size-11 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 md:group-data-[collapsible=icon]:size-9"
          >
            {/* Two elements because the rail collapses and the drawn lockup cannot
                be split — but they are the SAME artwork at two of its sanctioned
                sizes, not a lockup composed here out of parts. Expanded shows the
                lockup as drawn; collapsed shows the symbol alone, which is what
                BRAND.md prescribes below the lockup's 88px minimum width. */}
            <BrandLogo
              variant="lockup"
              className="h-6 text-brand-solid group-data-[collapsible=icon]:hidden"
              title={null}
            />
            <BrandLogo
              variant="isotipo"
              className="hidden h-6 text-brand-solid group-data-[collapsible=icon]:block"
              title={null}
            />
          </a>
          <SidebarToggle />
        </div>
      </SidebarHeader>

      <SidebarContent aria-label={t("ui.navigation.navigation_menu")} role="navigation">
        {currentLocation && (
          <>
            <SidebarGroup className="py-0">
              <SidebarGroupContent>
                {/* Organizations as well as branches. It reads `navContext`
                    straight off the page props rather than taking a list here:
                    the organizations a person belongs to are not derivable from
                    the branches they staff, and correlating the two is the
                    server's job -- see SharedNavContext. */}
                <StaffContextSwitcher
                  testId="context-switcher"
                  onNavigate={onNavigate}
                  compact={collapsed}
                />
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator />
          </>
        )}

        {manageContext ? (
          <>
            <SidebarGroup className="py-0">
              <SidebarGroupContent>
                <SidebarMenu>
                  {BASE_NAV_ITEMS.filter(visible).map((item) => (
                    <NavRow
                      key={item.href}
                      item={item}
                      active={isActiveHref(url, item.href)}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {MANAGER_NAV_GROUPS.map((group) => {
              const items = group.items.filter(visible).sort(byRenderedLabel)

              // A heading over nothing is worse than no heading: every item in a
              // drawer can be gated off, and "Inventario y compras" with no links
              // under it advertises a door that is not there.
              if (items.length === 0) return null

              return (
                <SidebarGroup key={group.labelKey} className="py-0">
                  {/* Collapsed this label is hidden by the primitive, which is why
                      the separator above each group is not decoration: on the icon
                      rail it is the only thing left saying where a drawer starts. */}
                  <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                    {t(group.labelKey)}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((item) => (
                        <NavRow
                          key={item.href}
                          item={item}
                          active={isActiveHref(url, item.href)}
                          collapsed={collapsed}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              )
            })}
          </>
        ) : (
          <SidebarGroup className="py-0">
            <SidebarGroupContent>
              <SidebarMenu>
                {FLOOR_NAV_ITEMS.filter(visible).map((item) => (
                  <NavRow
                    key={item.href}
                    item={item}
                    active={isActiveHref(url, item.href)}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="gap-1">
        {onboardingProgress && <StaffOnboardingProgress progress={onboardingProgress} />}

        {isManager && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={collapsed ? t(manageContext ? "ui.navigation.back_to_floor" : "ui.navigation.management") : undefined}
              >
                <a
                  href={manageContext ? "/dashboard" : "/manage"}
                  onClick={onNavigate}
                  data-testid="manage-shortcut-button"
                >
                  {manageContext ? <ArrowLeft /> : <Settings2 />}
                  <span>
                    {t(manageContext ? "ui.navigation.back_to_floor" : "ui.navigation.management")}
                  </span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        <StaffNotifications />

        {user && <StaffUserMenu user={user} />}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
